import React, { useState, useRef, useEffect, useCallback } from 'react';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core';

import { createTextareaValidationFn } from 'utils/validations/validations';
import { uuid } from '../../../utils/functions';
import RichTextEditor from '../RichTextEditor/RichTextEditor';

function normalizeInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return value;
}

const useStyles = makeStyles(() => ({
  helpSection: {
    position: 'relative',
    marginBottom: '1.5rem'
  },
  inputError: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  inputCharCount: {
    position: 'absolute',
    top: 0,
    right: 0
  }
}));

const TextArea = props => {
  const { t, value, disabled, onChange, onValidationError, placeholder, maxCharactersAllowed } = props;
  const inputValidation = createTextareaValidationFn(maxCharactersAllowed);

  // Blur when user scrolls the page
  const ref = useRef();
  const classes = useStyles();

  useEffect(() => {
    const myRef = ref;
    if (ref.current && ref.current.addEventListener) {
      const onMousewheel = () => {
        myRef.current.blur();
      };
      ref.current.addEventListener('mousewheel', onMousewheel, { passive: true });
      return () => myRef.current.removeEventListener('mousewheel', onMousewheel);
    }
    return undefined;
  }, []);

  // Prevent null or undefined input
  const normalizedInput = normalizeInput(value);

  // Init state
  const [input, setInput] = useState(normalizedInput);
  const [characterCount, setCharacterCount] = useState(normalizedInput.length);

  // Init error
  const [errorMsg, setError] = useState(null);

  // Show new input when value prop is updated
  useEffect(() => {
    setInput(normalizedInput);
    setCharacterCount(normalizedInput.length);
  }, [normalizedInput]);

  useEffect(() => {
    const errorObj = value && maxCharactersAllowed ? inputValidation(value) : null;
    if (errorObj) {
      onValidationError({ value, error: errorObj });
    }
    setError(errorObj ? t(errorObj.key, { ...errorObj.args }) : null);
  }, [inputValidation, onValidationError, maxCharactersAllowed, t, value]);

  const onInput = useCallback(
    inputValue => {
      setInput(inputValue);
      onChange(inputValue);
      setCharacterCount(inputValue.length);
    },
    [onChange]
  );

  return (
    <>
      <RichTextEditor editorId={uuid()} ref={ref} placeholder={placeholder} disabled={disabled} value={input} onChange={onInput} error={!!errorMsg} />
      {(errorMsg || maxCharactersAllowed) && (
        <div className={classes.helpSection}>
          {errorMsg && (
            <FormHelperText className={classes.inputError} error>
              {errorMsg}
            </FormHelperText>
          )}
          {maxCharactersAllowed && (
            <FormHelperText className={classes.inputCharCount}>{`${characterCount} / ${maxCharactersAllowed}`}</FormHelperText>
          )}
        </div>
      )}
    </>
  );
};

TextArea.defaultProps = {
  value: '',
  disabled: false,
  t: k => k,
  inputValidation: () => null,
  onValidationError: e => e
};

export default TextArea;
